import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import Scramble from '../components/Scramble';
import { profile, work, projects } from '../lib/profile';
import { blogs, blogPath, formatDate, isPublished } from '../lib/blogs';
import useTitle from '../lib/useTitle';
import './Home.css';

function Name({ text }) {
  return (
    <h1 className="intro__name" aria-label={text}>
      {Array.from(text).map((c, i) => (
        <span key={i} aria-hidden="true" style={{ '--i': i }}>
          {c === ' ' ? ' ' : c}
        </span>
      ))}
    </h1>
  );
}

// Headings numbered in hex: "0x01 work"
function Section({ index, title, delay, children }) {
  return (
    <section className="section reveal" style={{ '--d': delay }}>
      <h2 className="section__title">
        <span className="section__marker" aria-hidden="true">
          0x{String(index).padStart(2, '0')}
        </span>
        {title}
      </h2>
      <ul className="section__list">{children}</ul>
    </section>
  );
}

function Row({ id, spot, title, meta, sub, note, links, href, to }) {
  const content = (
    <>
      <span className="row__head">
        <span className="row__title">
          <Scramble text={title} />
          {(href || to) && <ArrowUpRight className="row__arrow" size={13} aria-hidden="true" />}
        </span>
        {meta && <span className="row__meta">{meta}</span>}
      </span>
      {sub && <span className="row__sub">{sub}</span>}
      {note && <span className="row__note">{note}</span>}
      {links && (
        // keyed on the spotlight so following the hash again replays it
        <span className="row__links" key={spot || 0}>
          {links.map((l, n) => (
            <a key={l.href} href={l.href} target="_blank" rel="noopener noreferrer" style={{ '--n': n }}>
              {l.label}
              <ArrowUpRight size={12} aria-hidden="true" />
            </a>
          ))}
        </span>
      )}
    </>
  );

  let el;
  if (to) el = <Link className="row row--link" to={to}>{content}</Link>;
  else if (href)
    el = (
      <a className="row row--link" href={href} target="_blank" rel="noopener noreferrer">
        {content}
      </a>
    );
  else el = <div className={`row${spot ? ' is-spotlight' : ''}`}>{content}</div>;

  return (
    <li id={id} className="row__item">
      {el}
    </li>
  );
}

export default function Home() {
  useTitle(null);
  const latest = blogs.slice(0, 4);
  const { hash, key } = useLocation();
  const [spot, setSpot] = useState(null);

  // Deep links like /#hackthenorth: wait for the sections to rise in, glide
  // to the row, then light up its links.
  useEffect(() => {
    const id = decodeURIComponent(hash.slice(1));
    if (!id || !projects.some((p) => p.id === id)) return undefined;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const t = setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
      setSpot({ id, n: Date.now() });
    }, reduce ? 0 : 450);
    return () => clearTimeout(t);
  }, [hash, key]);

  return (
    <main id="content" className="home container">
      <header className="intro">
        <Name text={profile.name} />
        <p className="intro__role reveal" style={{ '--d': 1 }}>
          {profile.role}
          <span className="caret" aria-hidden="true" />
        </p>

        <div className="intro__body reveal" style={{ '--d': 2 }}>
          <p>
            I study Computer Science at the University of Waterloo and build and break
            systems lately AI focused with a strong intent to responsible disclose.
          </p>
          <p>
            I write about my research on{' '}
            <Link className="link" to="/blog">
              Brain Dump
            </Link>
            , the storage for my security knowledge.
          </p>
        </div>
      </header>

      <Section index={1} title="work" delay={3}>
        {work.map((w) => (
          <Row key={w.company} title={w.company} meta={w.dates} sub={w.role} note={w.note} />
        ))}
      </Section>

      <Section index={2} title="projects" delay={4}>
        {projects.map((p) => (
          <Row
            key={p.name}
            id={p.id}
            spot={spot && p.id && spot.id === p.id ? spot.n : 0}
            title={p.name}
            meta={p.tag}
            note={p.note}
            links={p.links}
            href={p.href}
            to={p.to}
          />
        ))}
      </Section>

      <Section index={3} title="writing" delay={5}>
        {latest.map((b) => (
          <Row
            key={b.id}
            title={b.title}
            meta={isPublished(b) ? formatDate(b.publishDate) : 'in progress'}
            note={b.excerpt}
            to={isPublished(b) ? blogPath(b) : undefined}
          />
        ))}
      </Section>
      <Link className="section__more reveal" style={{ '--d': 5 }} to="/blog">
        <Scramble text="all writing" /> →
      </Link>

    </main>
  );
}
